import routeConfig from "@config/routes";
import { ChevronLeft, ChevronRight, Handyman as HandymanIcon, Upload as UploadIcon } from "@mui/icons-material";
import { Paper } from "@mui/material";
import { useTheme as useAppTheme } from "@mui/material/styles";
import { Box, useTheme } from "@mui/system";
import { loadSampleDataset } from "@store/fileParsing/actions";
import { configSidebar } from "@store/options/actions";
import useTranslations from "containers/hooks/useLanguageHook";
import { useAppDispatch, useAppSelector } from "index";
import { useLocation, useNavigate } from "react-router-dom";
import { ResponsiveButton } from "../Components/ResponsiveButton";
import { useContentSize, useSidebarPaddings } from "../hooks/useWindowSize";

function BottomBar() {
  const theme = useTheme();
  const appTheme = useAppTheme();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const nonDemoProvider = useAppSelector((s) => s.data.exports.filter((e) => e.providerId !== "sample"));
  const numberProvider = useAppSelector((s) => s.data.exports.length);
  const configSidebarOpen = useAppSelector((s) => s.options.configSidebarOpen);
  const activeFilters = useAppSelector((s) => s.filters.active);
  const filtersActiveWithChanges = useAppSelector((s) => {
    return activeFilters.some((filter) => {
      return s.filters[filter].some((filterState) => filterState.value === false);
    });
  });
  const contentSize = useContentSize();
  const paddings = useSidebarPaddings();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentRoute = getCurrentRoute();

  const nextRouteOptions = currentRoute ? getNextRouteOptions(currentRoute) : undefined;
  const previousRouteOptions = currentRoute ? getPreviousRouteOptions(currentRoute) : undefined;

  function getCurrentRoute() {
    let currentRoute = Object.entries(routeConfig.App).find(
      ([, routePath]) => routePath.split("/").slice(-1)[0] === location.pathname.split("/").slice(-1)[0],
    )?.[0];
    if (!currentRoute) {
      if (location.pathname.split("/").slice(-1)[0] === `app`) {
        currentRoute = routeConfig.Application;
      }
    }
    return currentRoute;
  }

  function getNextRouteOptions(currentRoute: string) {
    let nextRoute = "",
      nextRouteText = "";
    if (currentRoute === routeConfig.Application) {
      nextRoute = routeConfig.Application + routeConfig.App[routeConfig.VisualizationOrder[0]];
      nextRouteText = t.UI.Explore;
    } else {
      nextRoute =
        routeConfig.App[routeConfig.VisualizationOrder[routeConfig.VisualizationOrder.indexOf(currentRoute) + 1]];
      if (nextRoute) {
        nextRoute = routeConfig.Application + nextRoute;
      }
      nextRouteText = t.UI.NextVisualization;
    }

    return {
      route: nextRoute,
      text: nextRouteText,
    };
  }

  function getPreviousRouteOptions(currentRoute: string) {
    let previousRoute = "",
      previousRouteText = "";
    if (routeConfig.VisualizationOrder.indexOf(currentRoute) === -1) {
      previousRoute = null;
    } else if (routeConfig.VisualizationOrder.indexOf(currentRoute) === 0) {
      previousRoute = routeConfig.Application;
      previousRouteText = t.UI.AddData;
    } else {
      previousRoute =
        routeConfig.App[routeConfig.VisualizationOrder[routeConfig.VisualizationOrder.indexOf(currentRoute) - 1]];
      if (previousRoute) {
        previousRoute = routeConfig.Application + previousRoute;
      }
      previousRouteText = t.UI.PreviousVisualization;
    }
    return {
      route: previousRoute,
      text: previousRouteText,
    };
  }

  return (
    <Paper
      sx={{
        width: contentSize.width,
        height: appTheme.sidebar.bottomBarHeight,
        m: 0,
        borderRadius: 0,
        p: theme.spacing(0.5),
        position: "fixed",
        bottom: 0,
        left: paddings.left,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: appTheme.zIndex.drawer + 1,
      }}
    >
      <ResponsiveButton
        variant="contained"
        onClick={() => navigate(previousRouteOptions?.route ?? routeConfig.Application)}
        disabled={numberProvider === 0 || !previousRouteOptions?.route}
        text={previousRouteOptions?.text}
        icon={<ChevronLeft />}
        iconLocation={"start"}
        breakpoint={"sm"}
        sx={{ m: 1 }}
      />

      <Box sx={{ flexGrow: 1 }} />

      {!previousRouteOptions?.route && (
        <>
          <ResponsiveButton
            variant="contained"
            color="secondary"
            onClick={() => dispatch(loadSampleDataset())}
            text={t.DataPage.DemoDataset}
            icon={<UploadIcon />}
            breakpoint={"md"}
            sx={{ m: 1 }}
          />

          <ResponsiveButton
            variant="contained"
            onClick={() => navigate(`wrangler/${nonDemoProvider[0].id}`)}
            disabled={nonDemoProvider.length === 0}
            text={t.DataPage.Wrangling}
            tooltip={nonDemoProvider.length === 0 && numberProvider > 0 ? t.DataPage.WranglingWithProvider : undefined}
            icon={<HandymanIcon />}
            breakpoint={"lg"}
            sx={{ m: 1 }}
            color="secondary"
          />
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <ResponsiveButton
        variant="contained"
        onClick={() => navigate(nextRouteOptions?.route ?? routeConfig.Application)}
        disabled={numberProvider === 0 || !nextRouteOptions?.route}
        text={nextRouteOptions?.text}
        icon={<ChevronRight />}
        iconLocation={"end"}
        breakpoint={"sm"}
        sx={{ m: 1 }}
      />

      {!configSidebarOpen && (
        <Box>
          <ResponsiveButton
            variant="text"
            breakpoint="lg"
            onClick={() => dispatch(configSidebar())}
            tooltip={
              configSidebarOpen
                ? t.UI.Collapse
                : `${t.UI.ExpandConfigSidebar}${filtersActiveWithChanges ? ` (${t.Vis.FiltersActive})` : ``}`
            }
            text={configSidebarOpen ? t.UI.Collapse : t.UI.ConfigSidebar}
            icon={
              configSidebarOpen ? (
                <UploadIcon sx={{ transform: "rotate(90deg)" }} />
              ) : (
                <UploadIcon sx={{ transform: "rotate(-90deg)" }} />
              )
            }
            iconLocation={"start"}
            sx={{
              maxWidth: appTheme.sidebar.config.width,
            }}
          />
          {/* shows a "hint" (colored notification dot) if there are modified filters active and the sidebar is collapsed */}
          {filtersActiveWithChanges ? (
            <span
              style={{
                position: "absolute",
                top: "0.5rem",
                right: "0.75rem",
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "9999px",
                backgroundColor: appTheme.palette.warning.main,
              }}
            ></span>
          ) : null}
        </Box>
      )}
    </Paper>
  );
}

export { BottomBar };
